import React from 'react';
import {
    MapPin, Camera, User, Car, Activity, Info, Briefcase,
    FileText, Image as ImageIcon, Eye, Lock,
} from 'lucide-react';
import { Incarico } from '../../types';
import AlertRischi from '../AlertRischi';

interface Props {
    activeIncarico: Incarico;
    datiSoggetto: any;
    onOpenPreview: (file: any, fromField: boolean) => void;
}

// Esclude dalla galleria i file privati del cliente e i frame estratti dai video
// nell'editor relazione. Stesso filtro di TabMedia e FileManagerView.
const PRIVATE_FILE_RE = /^(datisoggetto_|privacy_|docid_|frame_)/i;
// Categorie che non fanno parte del mandato (es. documentazione d'ufficio):
// nella galleria entrano solo i file senza categoria.
const OFFICE_CATEGORIES = new Set(['DOC. UFFICIO', 'DOCUMENTAZIONE']);
const isPublicMandatoPhoto = (f: any) =>
    ['jpg', 'jpeg', 'png', 'webp'].includes(f.extension?.toLowerCase())
    && !PRIVATE_FILE_RE.test(f.name || '')
    && !OFFICE_CATEGORIES.has((f.categoria || '').trim().toUpperCase());

export const InfoTab: React.FC<Props> = ({ activeIncarico, datiSoggetto, onOpenPreview }) => {
    // Su incarico revocato non si mostra nulla del soggetto: solo il banner
    // con la data della revoca.
    const isRevocato = activeIncarico?.stato === 'REVOCATO';

    if (isRevocato) {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-8 flex flex-col items-center text-center gap-3 shadow-sm">
                    <Lock size={36} className="text-red-600" />
                    <h3 className="text-lg font-black uppercase tracking-wider text-red-800">
                        Incarico revocato dal titolare
                    </h3>
                    {activeIncarico?.dataRevoca && (
                        <p className="text-[11px] font-bold text-red-700/80 uppercase tracking-wider">
                            Revoca avvenuta il {new Date(activeIncarico.dataRevoca).toLocaleString('it-IT', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                            })}
                        </p>
                    )}
                    <p className="text-sm text-red-700/90 max-w-md leading-relaxed">
                        Le informazioni sul soggetto investigato non sono più consultabili.
                        Puoi solo visualizzare i media già caricati dal tab <strong>Archivio</strong>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
            <AlertRischi dati={datiSoggetto} />

            {/* 0. Mandato — Interesse / diritto tutelato (finalità giuridica dell'incarico) */}
            {datiSoggetto?.interesse_diritto_tutelato && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-indigo-50 border-b border-indigo-100 px-5 py-4 flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl"><Briefcase size={20} /></div>
                        <div>
                            <h3 className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mb-0.5">Mandato</h3>
                            <div className="text-sm font-black text-slate-900 leading-tight">Interesse / diritto tutelato</div>
                        </div>
                    </div>
                    <div className="px-5 py-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words">
                        {datiSoggetto.interesse_diritto_tutelato}
                    </div>
                </div>
            )}

            {/* 1. Anagrafica */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-orange-50 border-b border-orange-100 px-5 py-4 flex items-center gap-3">
                    <div className="p-2 bg-orange-100 text-orange-600 rounded-xl"><User size={20} /></div>
                    <div>
                        <h3 className="text-[10px] font-bold text-orange-600 uppercase tracking-widest mb-0.5">Anagrafica Obiettivo</h3>
                        <div className="text-xl font-black text-slate-900 leading-tight uppercase">
                            {datiSoggetto?.soggetto_nome || 'N/D'}
                            {datiSoggetto?.soggetto_tipo === 'Azienda' && (
                                <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded uppercase">Azienda</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="divide-y divide-slate-100">
                    {/* CF / P.IVA / Nascita: per Privato luogo e data sempre visibili ("N/D" se vuoti) */}
                    <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/50">
                        {datiSoggetto?.soggetto_tipo === 'Azienda' ? (
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">P.IVA / CF Azienda</span>
                                <span className="text-sm font-semibold text-slate-800 uppercase">{datiSoggetto.soggetto_piva || datiSoggetto.soggetto_codice_fiscale || 'N/D'}</span>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Luogo di nascita</span>
                                    <span className="text-sm font-semibold text-slate-800">
                                        {datiSoggetto?.soggetto_nato_a || 'N/D'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Data di nascita</span>
                                    <span className="text-sm font-semibold text-slate-800">
                                        {datiSoggetto?.soggetto_data_nascita
                                            ? new Date(datiSoggetto.soggetto_data_nascita).toLocaleDateString('it-IT')
                                            : 'N/D'}
                                    </span>
                                </div>
                                {datiSoggetto?.soggetto_codice_fiscale && (
                                    <div className="sm:col-span-2">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Codice Fiscale</span>
                                        <span className="text-sm font-semibold text-slate-800 uppercase">{datiSoggetto.soggetto_codice_fiscale}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Residenza */}
                    <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                        <div className="mt-0.5 text-blue-500 bg-blue-50 p-2.5 rounded-xl"><MapPin size={18} /></div>
                        <div className="flex-1">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                {datiSoggetto?.soggetto_tipo === 'Azienda' ? 'Via Sede Legale' : 'Residenza'}
                            </div>
                            <div className="text-sm font-semibold text-slate-800">
                                {datiSoggetto?.soggetto_tipo === 'Azienda' ? (
                                    <>
                                        {datiSoggetto?.soggetto_sede_via || 'Indirizzo non specificato'}
                                        {datiSoggetto?.soggetto_sede_citta && ` - ${datiSoggetto.soggetto_sede_cap || ''} ${datiSoggetto.soggetto_sede_citta} ${datiSoggetto.soggetto_sede_prov ? `(${datiSoggetto.soggetto_sede_prov})` : ''}`}
                                    </>
                                ) : (
                                    <>
                                        {datiSoggetto?.soggetto_indirizzo || 'Indirizzo non specificato'}
                                        {datiSoggetto?.soggetto_citta && ` - ${datiSoggetto.soggetto_cap || ''} ${datiSoggetto.soggetto_citta} ${datiSoggetto.soggetto_prov ? `(${datiSoggetto.soggetto_prov})` : ''}`}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Domicilio, solo per Privato */}
                    {datiSoggetto?.soggetto_tipo !== 'Azienda' && datiSoggetto?.soggetto_domicilio_altrove && (
                        <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                            <div className="mt-0.5 text-emerald-500 bg-emerald-50 p-2.5 rounded-xl"><MapPin size={18} /></div>
                            <div className="flex-1">
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Domicilio</div>
                                <div className="text-sm font-semibold text-slate-800 whitespace-pre-wrap">
                                    {datiSoggetto.soggetto_domicilio_altrove}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 2. Allegati fotografici mandato */}
            {activeIncarico?.pratica?.files?.some(isPublicMandatoPhoto) && (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                            <ImageIcon size={16} className="text-blue-500" /> Documentazione Fotografica Mandato
                        </h3>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {activeIncarico.pratica.files
                            .filter(isPublicMandatoPhoto)
                            .map((file: any) => (
                                <div
                                    key={file.id}
                                    className="group relative aspect-square bg-slate-100 rounded-xl overflow-hidden border border-slate-200 cursor-pointer hover:border-blue-400 transition-all"
                                    onClick={() => onOpenPreview(file, false)}
                                >
                                    <img
                                        src={file.urlCompressed || file.urlOriginal}
                                        alt={file.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=N.D.'; }}
                                    />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <Eye className="text-white" size={24} />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                        <p className="text-[9px] font-bold text-white truncate uppercase">{file.name}</p>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {/* 3. Profilo operativo */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                        <Briefcase size={16} /> Profilo Operativo
                    </h3>
                </div>
                <div className="divide-y divide-slate-100">
                    {/* Veicoli: del soggetto, dei familiari, motocicli */}
                    <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                        <div className="mt-0.5 text-indigo-500 bg-indigo-50 p-2.5 rounded-xl"><Car size={18} /></div>
                        <div className="flex-1 space-y-3">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Di proprietà</div>
                                <div className="text-sm font-semibold text-slate-800 uppercase whitespace-pre-wrap">
                                    {datiSoggetto?.soggetto_auto || datiSoggetto?.soggetto_mezzi || 'Nessun veicolo segnalato'}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Di famigliari</div>
                                <div className="text-sm font-medium text-slate-700 uppercase whitespace-pre-wrap">
                                    {datiSoggetto?.soggetto_auto_conoscenti || 'Nessun veicolo segnalato'}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Motocicli</div>
                                <div className="text-sm font-medium text-slate-700 uppercase whitespace-pre-wrap">
                                    {datiSoggetto?.soggetto_moto || 'Nessun motociclo segnalato'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Hobby e Abitudini */}
                    {(datiSoggetto?.soggetto_hobby || datiSoggetto?.soggetto_abitudini) && (
                        <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                            <div className="mt-0.5 text-teal-500 bg-teal-50 p-2.5 rounded-xl"><Activity size={18} /></div>
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {datiSoggetto?.soggetto_abitudini && (
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Abitudini / Orari tipici</span>
                                        <span className="text-sm font-medium text-slate-800">{datiSoggetto.soggetto_abitudini}</span>
                                    </div>
                                )}
                                {datiSoggetto?.soggetto_hobby && (
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Hobby e Sport</span>
                                        <span className="text-sm font-medium text-slate-800">{datiSoggetto.soggetto_hobby}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Fisico */}
                    {datiSoggetto?.soggetto_fisico && (
                        <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                            <div className="mt-0.5 text-rose-500 bg-rose-50 p-2.5 rounded-xl"><User size={18} /></div>
                            <div className="flex-1">
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Caratteristiche Fisiche</div>
                                <p className="text-sm font-medium text-slate-700 whitespace-pre-wrap">{datiSoggetto.soggetto_fisico}</p>
                            </div>
                        </div>
                    )}

                    {/* Luoghi frequentati */}
                    {datiSoggetto?.soggetto_luoghi_frequentati && (
                        <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                            <div className="mt-0.5 text-emerald-500 bg-emerald-50 p-2.5 rounded-xl"><MapPin size={18} /></div>
                            <div className="flex-1">
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Luoghi Frequentati</div>
                                <div className="text-sm text-slate-800 font-medium whitespace-pre-wrap bg-slate-100 p-2 rounded italic">"{datiSoggetto.soggetto_luoghi_frequentati}"</div>
                            </div>
                        </div>
                    )}

                    {/* Lavoro e contatti: il telefono del soggetto non si mostra al
                        collaboratore, per evitare chiamate accidentali sul campo. */}
                    {(datiSoggetto?.soggetto_lavoro || datiSoggetto?.soggetto_email || datiSoggetto?.soggetto_social) && (
                        <div className="p-4 sm:p-5 flex gap-4 items-start hover:bg-slate-50 transition-colors">
                            <div className="mt-0.5 text-amber-500 bg-amber-50 p-2.5 rounded-xl"><Info size={18} /></div>
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {datiSoggetto?.soggetto_lavoro && <div><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Attività Lavorativa</span><span className="text-sm font-medium text-slate-800">{datiSoggetto.soggetto_lavoro}</span></div>}
                                {datiSoggetto?.soggetto_email && <div><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Email</span><span className="text-sm font-medium text-slate-800">{datiSoggetto.soggetto_email}</span></div>}
                                {datiSoggetto?.soggetto_social && <div><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Social Network</span><span className="text-sm font-medium text-blue-600">{datiSoggetto.soggetto_social}</span></div>}
                            </div>
                        </div>
                    )}

                    {/* Note aggiuntive */}
                    {(datiSoggetto?.soggetto_altre_info || datiSoggetto?.soggetto_altre_info_casa || datiSoggetto?.soggetto_allegati_nomi) && (
                        <div className="p-4 sm:p-5 bg-slate-50/50 space-y-4">
                            {datiSoggetto?.soggetto_altre_info_casa && (
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Info Abitazione / Nucleo Familiare</span>
                                    <p className="text-sm text-slate-700 bg-white p-3 border border-slate-200 rounded-lg shadow-sm whitespace-pre-wrap">{datiSoggetto.soggetto_altre_info_casa}</p>
                                </div>
                            )}
                            {datiSoggetto?.soggetto_altre_info && (
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Altre Informazioni Utili</span>
                                    <p className="text-sm text-slate-700 bg-white p-3 border border-slate-200 rounded-lg shadow-sm whitespace-pre-wrap">{datiSoggetto.soggetto_altre_info}</p>
                                </div>
                            )}
                            {/*datiSoggetto?.soggetto_allegati_nomi && (
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Note Allegati Forniti</span>
                                    <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><ImageIcon size={14} /> {datiSoggetto.soggetto_allegati_nomi}</p>
                                </div>
                            )*/}
                        </div>
                    )}
                </div>
            </div>

            {/* 4. Direttive ufficio */}
            <div className="p-5 bg-blue-50/50 border border-blue-200/60 rounded-2xl">
                <h3 className="text-[10px] font-bold text-blue-800 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <FileText size={14} /> Note di Servizio (da Ufficio)
                </h3>
                <p className="text-sm text-blue-900 leading-relaxed font-semibold italic whitespace-pre-wrap">
                    "{activeIncarico?.note || 'Nessuna direttiva specifica presente. Seguire protocollo standard.'}"
                </p>
            </div>
        </div>
    );
};
